import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BookmarkPlus,
  Library,
  Pin,
  PinOff,
  Play,
  RefreshCcw,
  Sparkles,
  Trash2,
} from "lucide-react";
import {
  deleteSavedQuestion,
  listSavedQuestions,
  updateSavedQuestion,
  type SavedQuestion,
} from "@/lib/api";
import { useChatStore } from "@/store/chat";
import { PromptLibraryDialog } from "@/components/promptLibrary";

/**
 * Pinned + named natural-language questions. Re-running pushes the
 * question back into chat (via the chat store) and navigates to /chat
 * — same path the composer takes, so the answer renders through the
 * normal streaming pipeline.
 */
export function SavedQuestionsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { resetConversation } = useChatStore();
  const [libraryOpen, setLibraryOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery<SavedQuestion[]>({
    queryKey: ["saved-questions"],
    queryFn: listSavedQuestions,
  });

  const togglePin = useMutation({
    mutationFn: (q: SavedQuestion) => updateSavedQuestion(q.id, { pinned: !q.pinned }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-questions"] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteSavedQuestion(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-questions"] }),
  });

  function rerun(q: SavedQuestion) {
    // Drop into a fresh conversation so prior context doesn't bias the
    // re-run. ChatPage reads the URL hash for the prefill question.
    resetConversation();
    navigate("/chat#prefill=" + encodeURIComponent(q.question));
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-datumcraft-navy">Saved questions</h1>
          <p className="mt-0.5 text-sm text-datumcraft-slate">
            Pinned natural-language questions you can re-run later or attach to a digest.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-tour-id="saved-library"
            onClick={() => setLibraryOpen(true)}
            className="inline-flex items-center gap-1 rounded-md bg-datumcraft-navy px-3 py-1 text-xs font-medium text-white shadow-sm hover:bg-datumcraft-navy/90"
            title="Browse the curated prompt library"
          >
            <Library size={11} />
            Browse library
          </button>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-1 rounded-md border border-datumcraft-slate/30 bg-white px-3 py-1 text-xs font-medium text-datumcraft-ink hover:bg-datumcraft-mist"
          >
            <RefreshCcw size={11} />
            Refresh
          </button>
        </div>
      </header>

      <PromptLibraryDialog open={libraryOpen} onClose={() => setLibraryOpen(false)} />

      {isLoading && <p className="text-sm text-datumcraft-slate">Loading…</p>}
      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          Failed to load — {(error as Error).message}
        </p>
      )}

      {data && data.length === 0 && (
        <div className="rounded-xl border border-dashed border-datumcraft-slate/30 bg-white p-8 text-center text-sm text-datumcraft-slate">
          <BookmarkPlus className="mx-auto mb-2" size={28} />
          <p>
            No saved questions yet. Use the <Sparkles size={11} className="inline" /> Save button
            on a chat answer to pin one.
          </p>
          <p className="mt-3">
            Or try the{" "}
            <button
              type="button"
              onClick={() => setLibraryOpen(true)}
              className="font-medium text-datumcraft-navy underline-offset-2 hover:underline"
            >
              prompt library
            </button>
            {" "}— curated questions covering every Operate tool, one click each.
          </p>
        </div>
      )}

      {data && data.length > 0 && (
        <ul data-tour-id="saved-list" className="grid gap-2">
          {data.map((q) => (
            <li
              key={q.id}
              className={`flex flex-col gap-2 rounded-md border bg-white p-3 ${
                q.pinned ? "border-datumcraft-accent/50" : "border-datumcraft-slate/20"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {q.pinned && (
                      <Pin size={12} className="shrink-0 text-datumcraft-accent" aria-label="pinned" />
                    )}
                    <span className="font-medium text-datumcraft-ink truncate">{q.name}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-datumcraft-slate line-clamp-2">{q.question}</p>
                  <p className="mt-1 text-2xs text-datumcraft-slate">
                    Created {q.createdAt ? new Date(q.createdAt).toLocaleString() : "—"}
                    {q.lastRunAt && ` · Last run ${new Date(q.lastRunAt).toLocaleString()}`}
                    {q.runCount > 0 && ` · ${q.runCount} runs`}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => rerun(q)}
                    className="inline-flex items-center gap-1 rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1 text-2xs font-medium text-datumcraft-ink hover:bg-datumcraft-mist"
                    title="Re-run as a new conversation"
                  >
                    <Play size={11} />
                    Re-run
                  </button>
                  <button
                    type="button"
                    onClick={() => togglePin.mutate(q)}
                    className="inline-flex items-center gap-1 rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1 text-2xs font-medium text-datumcraft-ink hover:bg-datumcraft-mist"
                    title={q.pinned ? "Unpin" : "Pin"}
                  >
                    {q.pinned ? <PinOff size={11} /> : <Pin size={11} />}
                    {q.pinned ? "Unpin" : "Pin"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Delete "${q.name}"?`)) remove.mutate(q.id);
                    }}
                    className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-1 text-2xs font-medium text-rose-700 hover:bg-rose-50"
                    title="Delete"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
